import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultMST1020_R01HeaderDto, MST1020_R01HeaderDto } from './Dto_MST1020_R01';
import { useMST1020_R01Store } from './Store_MST1020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST1020_R01-item-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST1020_R01HeaderDto> | null>;
}

export const ItemTable = (props: Props) => {
	const { gridRef } = props;

	const {
		MST1020_R01ItemList,
		selectedMST1020_R01Item,
		setSelectedMST1020_R01Item,
		initSelectedMST1020_R01Item,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useMST1020_R01Store();

	const { brandCdList, itemGroupList, itemAccountList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: MST1020_R01ItemList,
		defaultGridData: defaultMST1020_R01HeaderDto,
		selectedRow: selectedMST1020_R01Item,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		modelNo: '모델명',
		brand: '브랜드',
		itemGroup: '품목그룹',
		itemAccount: '품목계정',
		stockQty: '재고량',
		hasChildYn: '등록여부',
		remark: '비고',
	};

	const createCodeToNameRenderer = useCallback((
		codeList: { minorCd: string; codeNm: string }[],
		field: keyof MST1020_R01HeaderDto,
	) => (params: { data?: MST1020_R01HeaderDto }) => {
		if (!params.data) return '';
		const codeValue = params.data[field];
		if (codeValue === null || codeValue === undefined) return '';
		const foundItem = codeList.find((item) => item.minorCd === codeValue);
		return foundItem ? foundItem.codeNm : String(codeValue);
	}, []);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<MST1020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'modelNo'),
		},
		{
			headerName: '브랜드', colId: 'brand', field: 'brandCd', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(brandCdList, 'brandCd'),
			cellStyle: (p) => getCellStyle(p, 'brand'),
		},
		{
			headerName: '품목그룹', colId: 'itemGroup', field: 'itemGroup', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(itemGroupList, 'itemGroup'),
			cellStyle: (p) => getCellStyle(p, 'itemGroup'),
		},
		{
			headerName: '품목계정', colId: 'itemAccount', field: 'itemAccount', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(itemAccountList, 'itemAccount'),
			cellStyle: (p) => getCellStyle(p, 'itemAccount'),
		},
		{
			headerName: '재고량', colId: 'stockQty', field: 'stockQty', width: 80,
			headerClass: aggridHeaderClass,
			cellRenderer: (p: { data?: MST1020_R01HeaderDto }) => p.data?.stockQty?.toLocaleString(),
			cellStyle: (p) => getCellStyle(p, 'stockQty'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '등록여부', colId: 'hasChildYn', width: 90,
			headerClass: aggridHeaderClass,
			valueGetter: (p) => p.data?.hasChildYn,
			cellStyle: (p) => getCellStyle(p, 'hasChildYn'),
			cellRenderer: (p: { node: { rowPinned?: string | null }; data?: MST1020_R01HeaderDto }) => {
				if (p.node.rowPinned === 'bottom') return '';
				const value = p.data?.hasChildYn;
				return value === 'Y' ? 'Y' : 'N';
			},
		},
	], [getCellStyle, createCodeToNameRenderer, brandCdList, itemGroupList, itemAccountList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST1020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST1020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST1020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData?.itemCd) return;

		const isSelected = selectedMST1020_R01Item?.itemCd === rowData.itemCd;
		setSelectedMST1020_R01Item(isSelected ? undefined : rowData);
		setDetailScrollTop(null);
	}, [selectedMST1020_R01Item, setSelectedMST1020_R01Item, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		itemCd: `${MST1020_R01ItemList.length.toLocaleString()}건`,
		stockQty: MST1020_R01ItemList.reduce((sum, item) => sum + item.stockQty, 0),
		editable: false,
		hasChildYn: '',
	}]), [MST1020_R01ItemList]);

	useEffect(() => { ensureSelectedRow(); }, [MST1020_R01ItemList, selectedMST1020_R01Item]);

	return (
		<GridContextWrapper<MST1020_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			initSelected={initSelectedMST1020_R01Item}
			defaultRowData={defaultMST1020_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST1020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST1020_R01HeaderDto>({
					idKey: 'itemCd',
					selectedList: selectedMST1020_R01Item ? [selectedMST1020_R01Item.itemCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
